import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { biometricUnlock, biometricUnlockAvailable, isNative, onNativeResume } from "@cntrl/native";
import { useApp } from "../appContext.js";
import { LockScreen } from "./LockScreen.js";

/** The OS-prompt reason string (shown in the Face ID / passcode sheet). */
const UNLOCK_REASON = "Unlock CNTRL";

/**
 * The Face-ID app-unlock gate ('s third acceptance clause; "the Face ID gate wraps
 * TokenProvider unlock"). Wraps the authed app tree in {@link Bootstrap}: on NATIVE with a HELD session the app
 * stays locked behind a successful biometric (or device-credential) auth before authed content is revealed —
 * on cold launch AND on every resume from background. Web and the unauthenticated /login surface are never
 * gated.
 *
 * Auth is read LIVE at each decision point, never sampled once: {@link TokenProvider.isAuthenticated} is
 * non-reactive and this component does not re-render on a login/logout (it sits above the router), so a
 * mount-time snapshot would (a) miss a session created by an in-app login — leaving the whole post-login run
 * un-re-locked — and (b) after logout re-lock the /login surface behind the prior principal's Face ID. So the
 * cold-launch lock and EVERY resume handler re-check `shouldGate()` afresh, and the resume listener is
 * subscribed unconditionally on native (a no-op unsubscribe on web) so a later login is still covered.
 *
 * Fail-open on the ENTRY gate (a device with no enrolled factor unlocks rather than bricking) lives in the
 * seam ({@link biometricUnlockAvailable} false ⇒ treat as unlocked); the session is still Keychain-protected.
 */
export function BiometricGate({ children }: { children: ReactNode }) {
  const { tokenProvider } = useApp();

  // Live, re-evaluated at each decision point (never a once-sampled snapshot). tokenProvider is the stable
  // bootstrap singleton, so this callback is itself stable.
  const shouldGate = useCallback(() => isNative() && tokenProvider.isAuthenticated(), [tokenProvider]);

  const [locked, setLocked] = useState(() => shouldGate());
  const [prompting, setPrompting] = useState(false);
  // Serialize prompts: a resume firing mid-prompt (or a double-tapped Unlock) must not stack OS sheets.
  const promptingRef = useRef(false);

  const attemptUnlock = useCallback(async () => {
    if (promptingRef.current) return;
    promptingRef.current = true;
    setPrompting(true);
    try {
      // No usable factor on the device ⇒ don't brick it: treat as unlocked (fail-open entry gate). Otherwise a
      // successful OS auth unlocks; a cancel/failure leaves it locked with the retry affordance.
      const ok = (await biometricUnlockAvailable()) ? await biometricUnlock(UNLOCK_REASON) : true;
      if (ok) setLocked(false);
    } finally {
      promptingRef.current = false;
      setPrompting(false);
    }
  }, []);

  // Cold launch: prompt once when a session is ALREADY held at mount (native + Keychain). A session created
  // later by an in-app login is NOT prompted immediately (the user just authenticated) — the resume re-lock
  // below protects it from the next backgrounding onward. shouldGate/attemptUnlock are stable ⇒ runs once.
  useEffect(() => {
    if (shouldGate()) void attemptUnlock();
  }, [shouldGate, attemptUnlock]);

  // Re-lock on EVERY resume from background — subscribed unconditionally on native (so a post-mount login is
  // covered), a no-op on web. The handler re-reads LIVE auth: lock only while a session is held; an
  // unauthenticated resume (e.g. after logout, on /login) explicitly clears any lock so the prior principal's
  // Face ID never gates the login surface. Presenting the biometric sheet does NOT background the app, so this
  // can't loop.
  useEffect(() => {
    return onNativeResume(() => {
      if (shouldGate()) {
        setLocked(true);
        void attemptUnlock();
      } else {
        setLocked(false);
      }
    });
  }, [shouldGate, attemptUnlock]);

  if (!locked) return <>{children}</>;
  return <LockScreen prompting={prompting} onUnlock={() => void attemptUnlock()} />;
}
